import React, { useMemo, useState } from "react";

// Helper function to format numbers as currency/decimal
const formatNumber = (val, decimals = 2) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Checks if a column header represents a numeric value that should be right-aligned
 * and formatted.
 */
const isNumericHeader = (header) => {
  const upperHeader = header.toUpperCase();

  const rule = getColumnRule(header);
  if (rule.decimals !== undefined || rule.numeric) return true;

  // Explicitly exclude descriptive keys
  if (
    upperHeader.includes("CODE") ||
    upperHeader.includes("NAME") ||
    upperHeader.includes("ID") ||
    upperHeader.includes("MAN") ||
    upperHeader.includes("CUSTOMER") ||
    upperHeader.includes("ACCOUNT") ||
    upperHeader.includes("GROUP") ||
    upperHeader.includes("DEPTNO") ||
    upperHeader.includes("DATE")
  ) {
    return false;
  }

  // Check if it contains common numeric keywords
  return [
    "SALES",
    "RETURN",
    "VAT",
    "COMMISSION",
    "NET",
    "AMT",
    "COST",
    "PRICE",
    "QTY",
    "AMOUNT",
    "PROFIT",
    "GROSS SALE",
    "DISC",
    "NET SALES",
  ].some((s) => upperHeader.includes(s));
};

const isProfitHeader = (header) => header.toUpperCase().includes("PROFIT");
const isDateHeader = (header) => header.toUpperCase().includes("DATE");

// Column overrides
const COLUMN_RULES = [
  { keys: ["CUSTOMER", "CUSTNAME"], width: "200px", truncate: true },
  { keys: ["DESCRIPTION"], width: "220px", truncate: true },
  { keys: ["%AGE", "PERCENTAGE", "PCT"], decimals: 2 },
  { keys: ["DEPTNO"], hidden: true },
];

const getColumnRule = (header) => {
  const upper = header.toUpperCase();
  return (
    COLUMN_RULES.find((rule) => rule.keys.some((k) => upper.includes(k))) ||
    {}
  );
};

const formatDate = (val) => {
  if (!val) return "N/A";
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const Spinner = ({ className = "h-5 w-5 text-gray-400" }) => (
  <svg
    className={`animate-spin ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

const ColumnExpandIcon = ({ expanded }) => (
  <svg
    className="w-3.5 h-3.5 shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {expanded ? (
      <path d="M9 3H5a2 2 0 0 0-2 2v4M15 3h4a2 2 0 0 1 2 2v4M9 21H5a2 2 0 0 1-2-2v-4M15 21h4a2 2 0 0 0 2-2v-4" />
    ) : (
      <path d="M3 9V5a2 2 0 0 1 2-2h4M21 9V5a2 2 0 0 0-2-2h-4M3 15v4a2 2 0 0 0 2 2h4M21 15v4a2 2 0 0 1-2 2h-4" />
    )}
  </svg>
);

const columnExpandStorageKey = (ariaLabel) =>
  `dynamicTable:${ariaLabel}:expandedColumns`;

const loadExpandedColumns = (ariaLabel) => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(columnExpandStorageKey(ariaLabel));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveExpandedColumns = (ariaLabel, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      columnExpandStorageKey(ariaLabel),
      JSON.stringify(value)
    );
  } catch {
    // Fail silently on storage errors
  }
};

const ExpandChevron = ({ expanded }) => (
  <svg
    className={`h-3 w-3 flex-shrink-0 text-indigo-400 transition-transform duration-150 ${
      expanded ? "rotate-180" : ""
    }`}
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

/**
 * DynamicTableForStockReport component matching the design language of DynamicTableForInvoiceProfitAnalysis.
 */
const DynamicTableForStockReport = ({
  rows,
  loading,
  loadNextPage,
  hasMore,
  stickyColumns = [],
  footerTotalKeys = [],
  ariaLabel = "data report",
  datagetTotalSeperateAPI,
}) => {
  // 1. Determine all display columns
  const allDisplayKeys = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const allKeys = Object.keys(rows[0]);

    // Filter out internal control keys
    const filteredKeys = allKeys.filter(
      (key) =>
        ![
          "isGroupHeader",
          "isSubHeader",
          "isSubSubHeader",
          "totalAmount",
          "totalAmountExclVAT",
          "YEAR",
        ].includes(key)
    );

    const remainingKeys = filteredKeys.filter(
      (key) => !stickyColumns.includes(key)
    );

    const visibleStickyColumns = stickyColumns.filter(
      (key) => !getColumnRule(key).hidden
    );
    const visibleRemainingKeys = remainingKeys.filter(
      (key) => !getColumnRule(key).hidden
    );

    return [...visibleStickyColumns, ...visibleRemainingKeys];
  }, [rows, stickyColumns]);

  const totalColumns = allDisplayKeys.length;

  const [expandedCells, setExpandedCells] = useState(() => new Set());
  const [expandedColumns, setExpandedColumns] = useState(() =>
    loadExpandedColumns(ariaLabel)
  );

  const toggleColumnExpanded = (columnKey) => {
    setExpandedColumns((prev) => {
      const next = { ...prev, [columnKey]: !prev[columnKey] };
      saveExpandedColumns(ariaLabel, next);
      return next;
    });
  };

  if (!rows || (rows.length === 0 && !loading)) return null;

  const toggleCellExpanded = (cellKey) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) {
        next.delete(cellKey);
      } else {
        next.add(cellKey);
      }
      return next;
    });
  };

  const cellValueClass = (columnKey, isExpanded, clickable) => {
    const rule = getColumnRule(columnKey);
    let truncateClass = "";
    if (rule.truncate) {
      const pointerClass = clickable ? "cursor-pointer" : "";
      truncateClass = isExpanded
        ? `${pointerClass} bg-violet-50 rounded-sm`
        : pointerClass;
    }
    if (!isNumericHeader(columnKey))
      return `text-left text-gray-700 ${truncateClass}`;
    return `text-right tabular-nums font-mono text-gray-700 ${truncateClass}`;
  };

  const columnStyle = (columnKey, isExpanded) => {
    const rule = getColumnRule(columnKey);
    if (!rule.width) return undefined;
    if (isExpanded) return { minWidth: rule.width };
    return { width: rule.width, maxWidth: rule.width };
  };

  const profitColorClass = (columnKey, value) => {
    if (!isProfitHeader(columnKey)) return "";
    return Number(value) < 0 ? "text-red-600" : "text-blue-700";
  };

  return (
    <div className="w-full relative">
      <table
        aria-label={ariaLabel}
        className="min-w-full divide-y divide-gray-200 text-sm leading-tight"
      >
        {allDisplayKeys.length > 0 && (
          <thead className="sticky top-0 z-10 border-b-2 border-indigo-300 bg-gradient-to-r from-violet-100 via-indigo-100 to-sky-100">
            <tr>
              {allDisplayKeys.map((columnKey) => {
                const rule = getColumnRule(columnKey);
                const isColumnExpanded = !!expandedColumns[columnKey];
                return (
                  <th
                    key={columnKey}
                    style={columnStyle(columnKey, isColumnExpanded)}
                    className={`px-2 py-1.5 text-xs font-bold text-indigo-900 uppercase tracking-wide whitespace-nowrap ${
                      isNumericHeader(columnKey) ? "text-right" : "text-left"
                    }`}
                  >
                    {rule.truncate ? (
                      <span className="inline-flex w-full items-center justify-between gap-1.5">
                        <span>
                          {columnKey.replace(/([A-Z])/g, " $1").trim()}
                        </span>
                        <button
                          type="button"
                          onClick={() => toggleColumnExpanded(columnKey)}
                          title={
                            isColumnExpanded
                              ? "Collapse this column back to fixed width"
                              : "Expand this column to show full width for every row"
                          }
                          className={`p-0.5 rounded hover:bg-white/70 transition-colors ${
                            isColumnExpanded
                              ? "text-violet-600 bg-white/60"
                              : "text-indigo-400"
                          }`}
                        >
                          <ColumnExpandIcon expanded={isColumnExpanded} />
                        </button>
                      </span>
                    ) : (
                      columnKey.replace(/([A-Z])/g, " $1").trim()
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr key={`dynamic-row-${index}`} className="hover:bg-gray-50">
              {allDisplayKeys.map((columnKey) => {
                const rule = getColumnRule(columnKey);
                const rawValue = row[columnKey];
                const displayValue = isDateHeader(columnKey)
                  ? formatDate(rawValue)
                  : isNumericHeader(columnKey)
                  ? formatNumber(rawValue, rule.decimals)
                  : rawValue === null || rawValue === undefined
                  ? "N/A"
                  : rawValue;

                const cellKey = `${index}-${columnKey}`;
                const isExpanded =
                  rule.truncate &&
                  (expandedColumns[columnKey] || expandedCells.has(cellKey));

                return (
                  <td
                    key={columnKey}
                    style={columnStyle(columnKey, isExpanded)}
                    title={
                      rule.truncate && !isExpanded && displayValue !== null
                        ? String(displayValue)
                        : undefined
                    }
                    onClick={
                      rule.truncate && !expandedColumns[columnKey]
                        ? () => toggleCellExpanded(cellKey)
                        : undefined
                    }
                    className={`px-2 py-1 ${
                      isExpanded ? "" : "whitespace-nowrap"
                    } ${cellValueClass(
                      columnKey,
                      isExpanded,
                      rule.truncate && !expandedColumns[columnKey]
                    )} ${profitColorClass(columnKey, rawValue)}`}
                  >
                    {rule.truncate ? (
                      <span className="inline-flex w-full items-center gap-1">
                        <span
                          className={
                            isExpanded
                              ? "underline decoration-dotted decoration-indigo-300"
                              : "truncate underline decoration-dotted decoration-indigo-300"
                          }
                        >
                          {displayValue}
                        </span>
                        <ExpandChevron expanded={isExpanded} />
                      </span>
                    ) : (
                      displayValue
                    )}
                  </td>
                );
              })}
            </tr>
          ))}

          {/* Load More Indicator */}
          {hasMore && (
            <tr>
              <td colSpan={totalColumns} className="text-center py-2.5">
                <div className="flex flex-col items-center gap-1">
                  <Spinner className="h-4 w-4 text-gray-400" />
                  <span className="text-[10px] text-gray-400 uppercase tracking-wide">
                    Loading more data...
                  </span>
                </div>
              </td>
            </tr>
          )}

          {!loading && rows.length === 0 && (
            <tr>
              <td
                colSpan={totalColumns}
                className="text-center py-4 text-xs text-gray-500 italic"
              >
                No data found.
              </td>
            </tr>
          )}
        </tbody>

       {/* Total Footer Row with sticky bottom & right alignment */}
{datagetTotalSeperateAPI && datagetTotalSeperateAPI.length > 0 && (
  <tfoot>
    <tr
      className="font-bold text-xs uppercase tracking-wide"
      style={{ borderTop: "3px double #111827" }}
    >
      <td
        colSpan={totalColumns}
        className="p-0 bg-gray-100 sticky bottom-0 z-10"
      >
        {/* Sticky right container aligns totals to the screen's right boundary */}
        <div className="sticky right-0 w-max ml-auto px-4 py-2 flex items-center gap-6 bg-gray-100 shadow-[-4px_0_6px_-1px_rgba(0,0,0,0.05)]">
          <span className="text-gray-700 font-extrabold">Total</span>
          <div>
            <span className="text-gray-500 mr-1.5">Qty:</span>
            <span className="tabular-nums font-mono text-gray-900">
              {formatNumber(datagetTotalSeperateAPI[0]?.Total_Qty)}
            </span>
          </div>
          <div>
            <span className="text-gray-500 mr-1.5">Value:</span>
            <span className="tabular-nums font-mono text-gray-900">
              {formatNumber(datagetTotalSeperateAPI[0]?.Total_Value)}
            </span>
          </div>
        </div>
      </td>
    </tr>
  </tfoot>
)}
      </table>

      {/* Global Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
          <Spinner className="h-8 w-8 text-gray-500" />
        </div>
      )}
    </div>
  );
};

export default DynamicTableForStockReport;